import { useMemo } from "react";
import {
  buildRelativeMonthRange,
  buildMonthRange,
  resolveStandardPeriodRange,
  type DateRange,
  type StandardPeriodFilter,
} from "@/utils/dateUtils";
import { useViewStateStore } from "@/stores/useViewStateStore";

type PeriodRangeFilter = StandardPeriodFilter | "all";

export const useStandardPeriodRange = <T extends PeriodRangeFilter = StandardPeriodFilter>(
  initialPeriod: NoInfer<T> = "this-month" as T,
  stateKey?: string,
) => {
  const storedRange = useViewStateStore((state) =>
    stateKey ? state.periodRanges[stateKey] : undefined,
  );
  const setPeriodRange = useViewStateStore((state) => state.setPeriodRange);
  const defaultRange = useMemo(
    () => ({
      selectedPeriod: initialPeriod,
      customStart: buildMonthRange(new Date()).start,
      customEnd: buildMonthRange(new Date()).end,
    }),
    [initialPeriod],
  );
  const rangeState = storedRange ?? defaultRange;
  const selectedPeriod = rangeState.selectedPeriod as T;
  const customStart = rangeState.customStart;
  const customEnd = rangeState.customEnd;

  const updateRange = (value: Partial<typeof rangeState>) => {
    if (!stateKey) return;
    setPeriodRange(stateKey, { ...rangeState, ...value });
  };

  const setSelectedPeriod = (value: T) => updateRange({ selectedPeriod: value });
  const setCustomStart = (value: string) => updateRange({ customStart: value });
  const setCustomEnd = (value: string) => updateRange({ customEnd: value });

  const selectedRange = useMemo<DateRange>(() => {
    if (selectedPeriod === "this-month" || selectedPeriod === "last-month" || selectedPeriod === "custom") {
      return resolveStandardPeriodRange(selectedPeriod, customStart, customEnd);
    }

    return buildRelativeMonthRange(0);
  }, [customEnd, customStart, selectedPeriod]);

  return {
    selectedPeriod,
    setSelectedPeriod,
    customStart,
    setCustomStart,
    customEnd,
    setCustomEnd,
    selectedRange,
  };
};
